import { SupportedLanguageType } from '@/utils/intl';
import { useEffect, useRef } from 'react';
import { SetMediaEl } from '../../types';

const styleVideoElement = {
    display: 'none',
};

interface AudioProps {
    sources?: { src: string; type: string }[];
    options?: {
        loop?: boolean;
        autoplay?: boolean;
        controls?: boolean;
    };
    onClose?: () => void;
    setHasAutoPlayRestriction: (hasAutoPlayRestriction: boolean) => void;
    subtitleSources?: { src: string; type: string }[];
    setMediaEl?: SetMediaEl;
    needsCredentials?: boolean;
}
export function Audio({
    options,
    sources,
    subtitleSources,
    setMediaEl,
    needsCredentials = false,
    onClose,
    setHasAutoPlayRestriction,
}: AudioProps) {
    const audioRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const audioEl = audioRef.current;
        setMediaEl?.(audioEl);

        return () => {
            audioEl?.pause();
            setMediaEl?.(null);
        };
    }, [audioRef, setMediaEl]);

    useEffect(() => {
        if (sources && audioRef.current) {
            // Remove existing sources
            audioRef.current.innerHTML = '';

            // Audio source
            sources.forEach(({ src, type }) => {
                const sourceElement = document.createElement('source');
                sourceElement.setAttribute('src', src);
                sourceElement.setAttribute('type', type);
                audioRef.current?.appendChild(sourceElement);
            });

            // Subtitles https://stackoverflow.com/questions/23475335/subtitles-for-audio-with-track-how-to-display-the-subtitles
            if (subtitleSources) {
                subtitleSources.forEach(({ src }) => {
                    const track = document.createElement('track');
                    track.src = src;
                    track.kind = 'captions';
                    track.srclang = SupportedLanguageType.EN;
                    track.track.mode = 'showing';
                    audioRef.current?.appendChild(track);
                });
            }
            if (options?.autoplay) {
                const autoPlayPromise = audioRef.current?.play();
                if (autoPlayPromise !== undefined) {
                    autoPlayPromise
                        .then(() => {
                            setHasAutoPlayRestriction(false);
                        })
                        .catch((error) => {
                            if (error.name === 'NotAllowedError') {
                                // Handle autoplay restriction
                                setHasAutoPlayRestriction(true);
                            }
                        });
                }
            }
        }
    }, [
        options?.autoplay,
        setHasAutoPlayRestriction,
        sources,
        subtitleSources,
    ]);

    // TODO Pause and play based on space, call onClose when escaped anywhere in the document, might not be needed
    useEffect(() => {
        const initHotKeys = async (e: KeyboardEvent) => {
            if (e.code === 'Space') {
                e.preventDefault();
                if (audioRef.current?.paused) {
                    await audioRef.current?.play();
                } else {
                    audioRef.current?.pause();
                }
            }
            if (e.code === 'Escape') {
                e.preventDefault();
                onClose?.();
            }
        };

        document?.addEventListener('keyup', initHotKeys);

        return () => {
            document?.removeEventListener('keyup', initHotKeys);
        };
    }, [onClose]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.loop = !!options?.loop;
        }
    }, [options?.loop]);

    return (
        <video
            style={styleVideoElement}
            ref={audioRef}
            crossOrigin={needsCredentials ? 'use-credentials' : 'anonymous'}
        />
    );
}
